import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, money } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT } from '../lang.js';
import Modal from '../Modal.jsx';
import Icon from '../Icon.jsx';
import PageSkeleton from '../Skeleton.jsx';

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

/**
 * Garsonun sipariş ekranı. Tek kural: AZ DOKUNUŞ.
 *
 *  · Ürüne dokunmak sepete ekler — araya onay ekranı girmez. Opsiyonu olan
 *    ürün kaçınılmaz olarak bir sayfa açar, olmayan ürün açmaz.
 *  · Sepet alttaki çubukta durur; çubuğa dokunmak satırları açar.
 *  · «Masadaki sipariş» aynı ekranda, tek dokunuş ötede: yanlış girilen
 *    kalem burada silinir (yetki sunucuda, `waiterMayEditOrder`).
 *  · Masanın hesabı yoksa ilk gönderimde kendiliğinden açılır; garsonun
 *    «önce masayı aç» diye ikinci bir ekrana uğraması gereksiz.
 */
export default function OrderEntry() {
  const { tableId } = useParams();
  const nav = useNavigate();
  const t = useT();

  const [menu, setMenu] = useState(null);
  const [table, setTable] = useState(null);
  const [me, setMe] = useState(null);
  const [cart, setCart] = useState([]);
  const [tab, setTab] = useState('menu');          // menu | current
  const [cat, setCat] = useState(0);               // 0 = tümü
  const [q, setQ] = useState('');
  const [sheetItem, setSheetItem] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const loadTable = () => api('/api/tables').then((d) => {
    setTable(d.tables.find((x) => String(x.id) === String(tableId)) || null);
  });
  const loadOrders = () => api(`/api/orders?tableId=${tableId}`).then(setOrders).catch(() => setOrders([]));

  useEffect(() => {
    Promise.all([
      api('/api/menu').then(setMenu),
      loadTable(),
      api('/api/restaurant').then(setMe),
    ]).catch((e) => setErr(errMessage(t, e)));
    loadOrders();
    // eslint-disable-next-line
  }, [tableId]);

  // Düzenleme yetkisi: sunucudaki kapının aynısı. Burada SAKLAMAK için değil,
  // çalışmayacak düğmeyi göstermemek için — reddi sunucu verir.
  const mayEdit = useMemo(() => {
    if (!me) return false;
    const role = me.me?.role;
    if (['OWNER', 'MANAGER', 'CASHIER'].includes(role)) return true;
    return role === 'WAITER' && me.settings?.waiterMayEditOrder !== false;
  }, [me]);

  const items = useMemo(() => {
    if (!menu) return [];
    const all = menu.categories.flatMap((c) => c.items.map((i) => ({ ...i, catId: c.id, catName: c.name })));
    const needle = q.trim().toLowerCase();
    return all
      .filter((i) => (cat ? i.catId === cat : true))
      .filter((i) => (needle ? i.name.toLowerCase().includes(needle) : true));
  }, [menu, cat, q]);

  const cartTotal = cart.reduce((a, c) => a + c.unit * c.qty, 0);
  const cartCount = cart.reduce((a, c) => a + c.qty, 0);
  const currency = menu?.currency || table?.session?.currency;

  function addItem(item, options = []) {
    const key = `${item.id}:${options.map((o) => o.id).sort().join(',')}`;
    const unit = item.priceMinor + options.reduce((a, o) => a + o.priceDeltaMinor, 0);
    setMsg(''); setErr('');
    setCart((c) => {
      const hit = c.find((x) => x.key === key);
      if (hit) return c.map((x) => (x.key === key ? { ...x, qty: x.qty + 1 } : x));
      return [...c, { key, item, options, qty: 1, unit }];
    });
    setSheetItem(null);
  }
  const bump = (key, d) => setCart((c) => c
    .map((x) => (x.key === key ? { ...x, qty: x.qty + d } : x))
    .filter((x) => x.qty > 0));

  async function send() {
    if (!cart.length || busy) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      // Hesabı yoksa aç. 409 = başkası az önce açmış; o da bizim için iyi.
      let sessionId = table?.session?.id || null;
      if (!sessionId) {
        try {
          const s = await api(`/api/tables/${tableId}/sessions`, { method: 'POST' });
          sessionId = s.id;
        } catch (e) {
          if (e.code !== 'table_already_open') throw e;
          await loadTable();
        }
      }
      await api('/api/orders', {
        method: 'POST',
        body: {
          tableId: Number(tableId),
          sessionId: sessionId || undefined,
          clientUuid: uuid(),           // çift dokunuş iki sipariş yazmasın
          items: cart.map((c) => ({
            menuItemId: c.item.id,
            qty: c.qty,
            optionIds: c.options.map((o) => o.id),
          })),
        },
      });
      setCart([]); setCartOpen(false);
      // «Önce ödeme» kipinde sipariş mutfağa GİTMEZ, ödeme bekler. Bunu
      // söylemezsek garson fişin neden çıkmadığını mutfağa sorar.
      setMsg(me?.settings?.orderingMode === 'PAY_FIRST' ? t('ord.sentPayFirst') : t('ord.sent'));
      await Promise.all([loadTable(), loadOrders()]);
    } catch (e) {
      setErr(errMessage(t, e));
    } finally {
      setBusy(false);
    }
  }

  async function voidItem(id) {
    setErr(''); setBusy(true);
    try {
      await api(`/api/orders/items/${id}/void`, { method: 'POST' });
      await Promise.all([loadOrders(), loadTable()]);
    } catch (e) { setErr(errMessage(t, e)); } finally { setBusy(false); }
  }

  if (err && !menu) return <p className="err">{err}</p>;
  if (!menu || !table) return <PageSkeleton shape="menu" />;

  const live = (orders || []).filter((o) => o.status !== 'CANCELED');
  const liveCount = live.flatMap((o) => o.items).filter((i) => i.status === 'ACTIVE').length;

  return (
    <div className="oe">
      <div className="oe-head">
        <button className="oe-back" onClick={() => nav('/tables')} aria-label={t('back')}><Icon n="arrowLeft" /></button>
        <div>
          <b>{table.name}</b>
          <span className="muted"> · {t(`tbl.status.${table.status}`)}</span>
          {table.session?.dueMinor > 0 && (
            <span className="muted"> · {t('tbl.remaining')} {money(table.session.dueMinor, table.session.currency)}</span>
          )}
        </div>
        <span className="spacer" />
        <div className="seg oe-seg" role="tablist">
          <button role="tab" aria-selected={tab === 'menu'} onClick={() => setTab('menu')}>
            <Icon n="book" />{t('ord.menu')}
          </button>
          <button role="tab" aria-selected={tab === 'current'} onClick={() => { setTab('current'); loadOrders(); }}>
            <Icon n="receipt" />{t('ord.current')}{liveCount ? ` (${liveCount})` : ''}
          </button>
        </div>
      </div>

      {tab === 'menu' ? (
        <>
          <div className="oe-filter">
            <input className="oe-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t('ord.search')} autoComplete="off" />
            <div className="chips">
              <button className={cat === 0 ? 'on' : ''} onClick={() => setCat(0)}>{t('ord.all')}</button>
              {menu.categories.map((c) => (
                <button key={c.id} className={cat === c.id ? 'on' : ''} onClick={() => setCat(c.id)}>{c.name}</button>
              ))}
            </div>
          </div>

          {items.length === 0 ? <p className="muted">{t('ord.noMatch')}</p> : (
            <div className="menugrid">
              {items.map((i) => (
                <button key={i.id} className="mtile" disabled={!i.available}
                  onClick={() => (i.optionGroups?.length ? setSheetItem(i) : addItem(i))}>
                  <span className="mname">{i.name}</span>
                  <span className="mprice">{money(i.priceMinor, currency)}</span>
                  {!i.available && <span className="moos">{t('ord.outOfStock')}</span>}
                  {!!i.optionGroups?.length && <span className="mopt">{t('ord.hasOptions')}</span>}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <CurrentOrders orders={live} t={t} mayEdit={mayEdit} busy={busy}
          onVoid={voidItem} currency={currency} />
      )}

      {msg && <p className="ok oe-flash">{msg}</p>}
      {err && <p className="err oe-flash">{err}</p>}

      {cart.length > 0 && (
        <>
          {cartOpen && (
            <div className="cartlist">
              {cart.map((c) => (
                <div key={c.key} className="cartrow">
                  <span className="cname">
                    {c.item.name}
                    {!!c.options.length && <em> · {c.options.map((o) => o.name).join(', ')}</em>}
                  </span>
                  <span className="cqty">
                    <button onClick={() => bump(c.key, -1)} aria-label="−"><Icon n="minus" /></button>
                    <b>{c.qty}</b>
                    <button onClick={() => bump(c.key, +1)} aria-label="+"><Icon n="plus" /></button>
                  </span>
                  <span className="cprice">{money(c.unit * c.qty, currency)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="cartbar">
            <button className="cartinfo" onClick={() => setCartOpen((o) => !o)}>
              <Icon n="cart" />
              <b>{t('ord.lines', { n: cartCount })}</b>
              <span>{money(cartTotal, currency)}</span>
            </button>
            <button className="primary cartsend" disabled={busy} onClick={send}>
              <Icon n="send" />{busy ? t('ord.sending') : t('ord.send')}
            </button>
          </div>
        </>
      )}

      {sheetItem && (
        <OptionSheet item={sheetItem} currency={currency} t={t}
          onClose={() => setSheetItem(null)} onAdd={(opts) => addItem(sheetItem, opts)} />
      )}
    </div>
  );
}

function CurrentOrders({ orders, t, mayEdit, busy, onVoid, currency }) {
  if (!orders.length) return <p className="muted">{t('ord.noOrders')}</p>;
  return (
    <div className="curlist">
      {orders.map((o) => (
        <div className="card" key={o.id}>
          <div className="row">
            <b>#{o.code}</b>
            <span className="badge">{t(`ord.status.${o.status}`)}</span>
            <span className="spacer" />
            <b>{money(o.totalMinor, o.currency || currency)}</b>
          </div>
          {o.items.map((i) => (
            <div key={i.id} className={i.status === 'VOID' ? 'currow void' : 'currow'}>
              {/* Misafirin kalem notu ("soğansız") burada da görünür: garson
                  masaya giderken fişi beklemek zorunda kalmasın. */}
              <span>{i.qty}× {i.nameSnapshot}{i.note ? ` · “${i.note}”` : ''}</span>
              <span className="spacer" />
              <span className="muted">{money(i.lineTotalMinor, o.currency || currency)}</span>
              {i.status === 'ACTIVE' && mayEdit && (
                <button className="btn-ico" disabled={busy} title={t('ord.void')} aria-label={t('ord.void')}
                  onClick={() => confirm(t('ord.confirmVoid', { name: i.nameSnapshot })) && onVoid(i.id)}>
                  <Icon n="trash" />
                </button>
              )}
              {i.paidMinor > 0 && <span className="badge ok">{t('ord.paid')}</span>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Opsiyonlu ürün: gruplar sunucunun kurallarıyla (zorunlu / en çok n) gösterilir. */
function OptionSheet({ item, currency, t, onClose, onAdd }) {
  const [picked, setPicked] = useState([]);
  const has = (o) => picked.some((p) => p.id === o.id);

  const max = (g) => g.maxSelect || 1;
  const countIn = (list, g) => list.filter((x) => x.groupId === g.id).length;

  function toggle(group, opt) {
    setPicked((p) => {
      if (p.some((x) => x.id === opt.id)) return p.filter((x) => x.id !== opt.id);
      // Tek seçimli grupta yeni seçim eskisinin YERİNE geçer: garson
      // "önce eskiyi kaldır" diye iki kez dokunmasın.
      if (max(group) === 1) return [...p.filter((x) => x.groupId !== group.id), { ...opt, groupId: group.id }];
      if (countIn(p, group) >= max(group)) return p;   // sınır dolu (düğme zaten kapalı)
      return [...p, { ...opt, groupId: group.id }];
    });
  }

  const missing = (item.optionGroups || []).filter((g) => {
    const n = picked.filter((p) => p.groupId === g.id).length;
    return (g.required && n < Math.max(1, g.minSelect)) || n < g.minSelect;
  });
  const total = item.priceMinor + picked.reduce((a, o) => a + o.priceDeltaMinor, 0);

  return (
    <Modal title={item.name} subtitle={money(total, currency)} onClose={onClose}
      footer={
        <button className="primary block" disabled={missing.length > 0} onClick={() => onAdd(picked)}>
          <Icon n={missing.length ? 'layers' : 'plus'} />
          {missing.length ? t('ord.pickRequired', { group: missing[0].name }) : t('ord.add')}
        </button>
      }>
      {(item.optionGroups || []).map((g) => (
        <div key={g.id} className="optgroup">
          <h3>
            {g.name}
            <span className="muted">
              {g.required ? ` · ${t('ord.required')}` : ''}
              {g.maxSelect > 1 ? ` · ${t('ord.upTo', { n: g.maxSelect })}` : ''}
            </span>
          </h3>
          <div className="optrow">
            {g.options.filter((o) => o.available).map((o) => (
              <button key={o.id} className={has(o) ? 'opt on' : 'opt'} onClick={() => toggle(g, o)}
                disabled={!has(o) && max(g) > 1 && countIn(picked, g) >= max(g)}>
                {o.name}
                {o.priceDeltaMinor !== 0 && <em> {o.priceDeltaMinor > 0 ? '+' : ''}{money(o.priceDeltaMinor, currency)}</em>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </Modal>
  );
}
